// src/api/apiClient.js
import { apiConfig } from "../config/api.config";

/**
 * Request genérico (Hostinger + PHP)
 * - Soporta JSON y x-www-form-urlencoded
 * - Mantiene cookies (credentials: include)
 */
export async function apiRequest(path, options = {}) {
  const isAbsolute = /^https?:\/\//i.test(path);
  const url = isAbsolute ? path : apiConfig.getUrl(path);

  const method = (options?.method || "GET").toUpperCase();
  console.log("🌐 apiRequest →", url, "method:", method);

  const token =
    localStorage.getItem("auth_token") ||
    sessionStorage.getItem("auth_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  const headers = { ...(options.headers || {}) };
  const body = options.body;

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  // Si NO es FormData y aún no definiste content-type, ponemos JSON por defecto
  if (!isFormData && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }

  // Bearer opcional (si lo usas); cookies siguen activas
  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    method,
    headers,
    credentials: "include",
  });

  // Manejo de sesión expirada
  if (response.status === 401) {
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("auth_user");
    sessionStorage.removeItem("auth_user");
    window.location.href = "/login";
    throw new Error("Sesión expirada");
  }

  // Si viene error, intenta leer texto/JSON para mostrarlo
  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    let details = "";
    try {
      if (contentType.includes("application/json")) {
        const j = await response.json();
        details = j?.error || j?.message || JSON.stringify(j);
      } else {
        details = await response.text();
      }
    } catch (_) {}
    throw new Error(`Error ${response.status}: ${details || response.statusText}`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return await response.json();
  return await response.text();
}

// ===============================
// CONTRATOS
// ===============================

export async function getContract(id) {
  const url = apiConfig.routes.contractGet(id);
  return apiRequest(url, { method: "GET" });
}

/**
 * CREATE (PHP/MySQL)
 * IMPORTANTÍSIMO:
 *  - tu tabla usa: modalidad_seleccion (no "modalidad")
 *  - tu tabla usa: valor (no "valor_total")
 */
export async function createContract(data) {
  const url = apiConfig.getUrl("/api/contracts/create/index.php");

  const body = {
    fecha_solicitud: data?.fecha_solicitud ?? data?.fechaSolicitud ?? "",
    centro_costos: data?.centro_costos ?? data?.centroCostos ?? "",
    secretaria: data?.secretaria ?? "",
    tipo_contrato: data?.tipo_contrato ?? data?.tipoContrato ?? "",

    // ✅ CLAVE: mandar exactamente modalidad_seleccion
    modalidad_seleccion:
      data?.modalidad_seleccion ??
      data?.modalidadSeleccion ??
      data?.modalidad ??
      data?.modalidad_seleccion_aprobada ??
      "",

    objeto_contractual: data?.objeto_contractual ?? data?.objetoContractual ?? "",

    // ✅ CLAVE: tu tabla usa "valor"
    valor:
      data?.valor ??
      data?.presupuesto_total ??
      data?.presupuestoTotal ??
      data?.valor_total ??
      0,

    nombre_solicitante:
      data?.nombre_solicitante ??
      data?.nombreSolicitante ??
      data?.solicitante ??
      "",
    cargo_solicitante: data?.cargo_solicitante ?? data?.cargoSolicitante ?? "",
  };

  console.log("📦 BODY createContract:", body);

  const resp = await apiRequest(url, {
    method: "POST",
    body: JSON.stringify(body),
  });

  const contract = resp?.contract || resp?.data || resp || null;

  const id =
    resp?.contract_id ??
    resp?.id ??
    contract?.id ??
    contract?.contract_id ??
    null;

  const radicado =
    resp?.radicado ??
    contract?.radicado ??
    contract?.numero_radicado ??
    null;

  return { ...resp, contract, id, radicado };
}

/**
 * UPDATE (PHP/MySQL)
 * POST + action=update (compatible con Hostinger)
 * y lo mando como x-www-form-urlencoded.
 */
export async function updateContract(id, data) {
  if (!id) throw new Error("updateContract(id): id requerido");

  const url = apiConfig.routes.contractUpdate(id);

  // ✅ NORMALIZAR todos los campos (snake_case a camelCase y viceversa)
  const normalized = {
    ...data,  // Mantener todos los campos que ya vienen
    // Normalizar campos específicos que pueden venir en ambos formatos
    modalidad_seleccion: data?.modalidad_seleccion ?? data?.modalidadSeleccion ?? data?.modalidad,
    valor: data?.valor ?? data?.presupuesto_total ?? data?.presupuestoTotal ?? data?.valor_total,
    nombre_solicitante: data?.nombre_solicitante ?? data?.nombreSolicitante ?? data?.solicitante,
    cargo_solicitante: data?.cargo_solicitante ?? data?.cargoSolicitante,
    objeto_contractual: data?.objeto_contractual ?? data?.objetoContractual,
    forma_pago: data?.forma_pago ?? data?.formaPago,
    fecha_solicitud: data?.fecha_solicitud ?? data?.fechaSolicitud,
    centro_costos: data?.centro_costos ?? data?.centroCostos,
    tipo_contrato: data?.tipo_contrato ?? data?.tipoContrato,
    duracion_estimada: data?.duracion_estimada ?? data?.duracionEstimada,
    nombre_contratista: data?.nombre_contratista ?? data?.nombreContratista,
    presupuesto_total: data?.presupuesto_total ?? data?.presupuestoTotal,
    naturaleza_gasto: data?.naturaleza_gasto ?? data?.naturalezaGasto,
    discriminacion_rubros: data?.discriminacion_rubros ?? data?.discriminacionRubros,
    fecha_inicio_contrato: data?.fecha_inicio_contrato ?? data?.fechaInicioContrato,
    fecha_fin_contrato: data?.fecha_fin_contrato ?? data?.fechaFinContrato,
    nombre_rubro: data?.nombre_rubro ?? data?.nombreRubro,
    numero_acta: data?.numero_acta ?? data?.numeroActa,
    fecha_comite: data?.fecha_comite ?? data?.fechaComite,
  };

  const form = new URLSearchParams();
  form.append("action", "update");
  form.append("id", String(id));
  form.append("data", JSON.stringify(normalized));

  return apiRequest(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: form.toString(),
  });
}

export async function advanceContract(data) {
  const url = apiConfig.routes.advance();
  return apiRequest(url, {
    method: "POST",
    body: JSON.stringify(data),
  });
}